import Decimal from 'decimal.js';
import { compact } from 'lodash';

import { getTypeOrUndefined } from '@/modules/entities/EntitySubforms/utils/shared/common.utils';
import { getEntityTypeFromSubtype } from '@/modules/entities/utils/getEntityTypeFromSubtype';
import { ContextPrimaryClient } from '@/modules/household/contexts/householdDetails.context';
import {
  AugmentedCreateDispositiveProvisionInput,
  AugmentedCreateDispositiveProvisionTemplateInput,
  AugmentedUpdateDispositiveProvisionTemplateInput,
  ClientProfileWhereInput,
  DispositionScenarioWhereInput,
  DispositiveProvisionDispositionKind,
  DispositiveProvisionTemplateKind,
  DispositiveProvisionTemplateSubKind,
  DispositiveProvisionTransferTaxKind,
  EntityWhereInput,
  TestamentaryEntityWhereInput,
  UpdateDispositiveProvisionTemplateInput,
} from '@/types/schema';
import { UnreachableError } from '@/utils/errors';
import { getNodes } from '@/utils/graphqlUtils';

import { DispositionScheme } from '../dispositiveProvisions.types';
import {
  mapProvisionToRecipient,
  sortDispositionOrder,
} from '../dispositiveProvisions.utils';
import {
  defaultRecipient,
  DISPOSITIVE_PROVISIONS_FORM_NAMESPACE,
} from '../DispositiveProvisionsForm/DispositiveProvisionsForm.constants';
import {
  DispositiveProvisionsFormRecipient,
  DispositiveProvisionsModalDetailsType,
  DispositiveProvisionsTemplateSlotKind,
  RecipientKind,
} from '../DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import {
  isRecipientWithId,
  makeCreateProvisionInputsFromRecipient,
} from '../DispositiveProvisionsForm/DispositiveProvisionsForm.utils';
import { SURVIVING_SPOUSE_SENTINEL } from '../DispositiveProvisionsForm/hooks/useRecipientOptions';
import { calculateResiduaryRecipients } from '../DispositiveProvisionsFormV2/components/formBody/primaryClientDeathSection/primaryClientDeathSection.utils';
import { DispositiveProvisionsTemplateLinkedEntitiesProps } from './DispositiveProvisionsTemplateSplitScreenModal.components';
import { BLANK_POUROVER_RECIPIENT } from './DispositiveProvisionsTemplateSplitScreenModal.constants';
import {
  DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE,
  DispositiveProvisionsTemplateLinkedEntryRow,
  DispositiveProvisionsTemplateShape,
  DispositiveProvisionTemplateDetails,
  MaritalTrustType,
  NEW_TEMPLATE_SENTINEL,
  ProRataValue,
} from './DispositiveProvisionsTemplateSplitScreenModal.types';
import { DispositiveProvisionTemplateData_DispositiveProvisionTemplateFragment } from './graphql/DispositiveProvisionTemplateData.generated';
import {
  DispositiveProvisionTemplateUseCount_ClientProfileFragment,
  DispositiveProvisionTemplateUseCount_EntityFragment,
  DispositiveProvisionTemplateUseCount_TestamentaryEntityFragment,
} from './graphql/DispositiveProvisionTemplateUseCount.generated';
import { getDefaultAbcTrustRecipients } from './presets/DispositiveProvisionsTemplateSplitScreenModal.abcTrust';
import { DEFAULT_AB_TRUST_RECIPIENTS } from './presets/DispositiveProvisionsTemplateSplitScreenModal.abTrust';
import { BLANK_MARITAL_TRUST } from './presets/DispositiveProvisionsTemplateSplitScreenModal.components';
import { BLANK_OUTRIGHT_TO_SURVIVING_SPOUSE } from './presets/DispositiveProvisionsTemplateSplitScreenModal.outrightToSurvivingSpouse';

function applySubKind(
  formData: DispositiveProvisionsTemplateShape
): DispositiveProvisionTemplateSubKind {
  const {
    templateType,
    maritalTrustType,
    includeSurvivorsTrust,
    includeDisclaimerTrust,
  } = formData[DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE];
  if (templateType === DispositiveProvisionTemplateKind.MaritalTrust) {
    if (maritalTrustType === MaritalTrustType.ClaytonQTIPTrust) {
      return DispositiveProvisionTemplateSubKind.ClaytonQtip;
    } else if (maritalTrustType === MaritalTrustType.DisclaimerTrust) {
      return DispositiveProvisionTemplateSubKind.Disclaimer;
    }
  } else if (templateType === DispositiveProvisionTemplateKind.AbTrust) {
    if (includeSurvivorsTrust) {
      return DispositiveProvisionTemplateSubKind.Survivor;
    }
  } else if (
    templateType === DispositiveProvisionTemplateKind.OutrightToSurvivingSpouse
  ) {
    if (includeDisclaimerTrust) {
      return DispositiveProvisionTemplateSubKind.Disclaimer;
    }
  }
  return DispositiveProvisionTemplateSubKind.None;
}

/**
 * When `residuaryPaysAllTaxes` is true, override each recipient's `bearsTax`
 * so that only residuary recipients bear taxes.  This mirrors the sync done
 * in `convertDPFormV2ScenarioToDPFormV1Scenario` for non-template forms.
 */
function syncBearsTaxWithResiduary(
  recipients: DispositiveProvisionsFormRecipient[],
  residuaryPaysAllTaxes: boolean | null
): DispositiveProvisionsFormRecipient[] {
  if (!residuaryPaysAllTaxes) return recipients;

  const { residuaryIndices } = calculateResiduaryRecipients(recipients);
  return recipients.map((r, idx) => ({
    ...r,
    bearsTax: residuaryIndices.includes(idx),
  }));
}

export function mapFormDataToCreatePayload(
  formData: DispositiveProvisionsTemplateShape,
  householdID: string
): AugmentedCreateDispositiveProvisionTemplateInput {
  const residuaryPaysAllTaxes =
    formData[DISPOSITIVE_PROVISIONS_FORM_NAMESPACE]._residuaryPaysAllTaxes ??
    true;

  const output: AugmentedCreateDispositiveProvisionTemplateInput = {
    create: {
      displayName:
        formData[DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE].displayName,
      documentID: getTypeOrUndefined(
        formData[DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE].documentId
      ),
      householdID,
      kind: formData[DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE]
        .templateType,
      subKind: applySubKind(formData),
      proRata:
        formData[DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE].proRata ===
        ProRataValue.PRO_RATA,
      residuaryPaysAllTaxes,
    },
    withDispositiveProvisions: compact(
      syncBearsTaxWithResiduary(
        formData[DISPOSITIVE_PROVISIONS_FORM_NAMESPACE].recipients,
        residuaryPaysAllTaxes
      ).flatMap((recipient) => {
        if (!isRecipientWithId(recipient)) return null;
        return makeCreateProvisionInputsFromRecipient(
          recipient,
          {
            householdId: householdID,
            isOnHypotheticalWaterfall: false,
            clientProfileId: '', // this isn't used by the consuming function in this case
          },
          householdID
        );
      })
    ).map((input, idx) => ({
      ...input,
      create: { ...input.create, dispositionOrder: idx },
    })),
  };
  return output;
}

export function mapFormDataToUpdatePayload(
  formData: DispositiveProvisionsTemplateShape,
  householdId: string
): AugmentedUpdateDispositiveProvisionTemplateInput {
  const residuaryPaysAllTaxes =
    formData[DISPOSITIVE_PROVISIONS_FORM_NAMESPACE]._residuaryPaysAllTaxes ??
    true;

  const update: UpdateDispositiveProvisionTemplateInput = {
    displayName:
      formData[DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE].displayName,
    clearDispositiveProvisions: true,
    subKind: applySubKind(formData),
    proRata:
      formData[DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE].proRata ===
      ProRataValue.PRO_RATA,
    residuaryPaysAllTaxes,
  };

  if (formData[DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE].documentId) {
    update.documentID =
      formData[DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE].documentId;
  } else {
    update.clearDocument = true;
  }

  const withDispositiveProvisions:
    | AugmentedCreateDispositiveProvisionInput[]
    | undefined = compact(
    syncBearsTaxWithResiduary(
      formData[DISPOSITIVE_PROVISIONS_FORM_NAMESPACE].recipients,
      residuaryPaysAllTaxes
    ).flatMap((recipient) => {
      if (!isRecipientWithId(recipient)) return null;
      return makeCreateProvisionInputsFromRecipient(
        recipient,
        {
          householdId,
          isOnHypotheticalWaterfall: false,
          clientProfileId: '', // this isn't used by the consuming function in this case
        },
        householdId
      );
    })
  ).map((input, idx) => ({
    ...input,
    create: { ...input.create, dispositionOrder: idx },
  }));

  const output: AugmentedUpdateDispositiveProvisionTemplateInput = {
    id: formData[DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE].id,
    update,
    withDispositiveProvisions,
  };

  return output;
}

/**
 * Assigns client-only slot identities to loaded A/B trust recipients without
 * backfilling slots that are absent from the saved template.
 */
function reconstructAbTrustRecipients(
  recipients: DispositiveProvisionsFormRecipient[],
  includeSurvivorsTrust: boolean
): DispositiveProvisionsFormRecipient[] {
  const gstExemptMaritalTrust = recipients.find(
    (recipient) =>
      recipient.dispositionKind_Select ===
      DispositiveProvisionDispositionKind.RemainingGstExclusionOfGrantorInExcessOfLifetimeExclusion
  );
  const gstNonExemptMaritalTrust = recipients.find(
    (recipient) =>
      recipient.dispositionKind_Select ===
      DispositiveProvisionDispositionKind.AnythingLeftOver
  );
  const leadingRecipients = recipients.filter(
    (recipient) =>
      recipient !== gstExemptMaritalTrust &&
      recipient !== gstNonExemptMaritalTrust
  );

  const mappedLeadingRecipients: DispositiveProvisionsFormRecipient[] =
    leadingRecipients.map((recipient, index) => {
      let slotKind = DispositiveProvisionsTemplateSlotKind.Generic;
      if (includeSurvivorsTrust && index === 0) {
        slotKind = DispositiveProvisionsTemplateSlotKind.AbSurvivor;
      } else if (
        (!includeSurvivorsTrust && index === 0) ||
        (includeSurvivorsTrust &&
          index === 1 &&
          recipient.dispositionKind_Select ===
            DispositiveProvisionDispositionKind.RemainingLifetimeExclusionOfGrantor)
      ) {
        slotKind = DispositiveProvisionsTemplateSlotKind.AbBypass;
      }
      return { ...recipient, _templateSlotKind: slotKind };
    });

  return [
    ...mappedLeadingRecipients,
    ...(gstExemptMaritalTrust
      ? [
          {
            ...gstExemptMaritalTrust,
            _templateSlotKind:
              DispositiveProvisionsTemplateSlotKind.AbGstExempt,
          },
        ]
      : []),
    ...(gstNonExemptMaritalTrust
      ? [
          {
            ...gstNonExemptMaritalTrust,
            _templateSlotKind:
              DispositiveProvisionsTemplateSlotKind.AbGstNonExempt,
          },
        ]
      : []),
  ];
}

function assignTemplateSlotKinds(
  recipients: DispositiveProvisionsFormRecipient[],
  kind: DispositiveProvisionTemplateKind,
  subKind: DispositiveProvisionTemplateSubKind,
  includeDisclaimerTrust: boolean
): DispositiveProvisionsFormRecipient[] {
  return recipients.map((recipient, index) => {
    if (recipient._templateSlotKind) return recipient;

    if (kind === DispositiveProvisionTemplateKind.AbcTrust) {
      const slotKindByDisposition: Partial<
        Record<
          DispositiveProvisionDispositionKind,
          DispositiveProvisionsTemplateSlotKind
        >
      > = {
        [DispositiveProvisionDispositionKind.LesserOfRemainingStateOrFederalExemption]:
          DispositiveProvisionsTemplateSlotKind.AbcCreditShelter,
        [DispositiveProvisionDispositionKind.RemainingFederalLifetimeExemptionInExcessOfStateExemption]:
          DispositiveProvisionsTemplateSlotKind.AbcFederalExemptStateNonExempt,
        [DispositiveProvisionDispositionKind.RemainingGstExclusionOfGrantorInExcessOfLifetimeExclusion]:
          DispositiveProvisionsTemplateSlotKind.AbcGstExempt,
        [DispositiveProvisionDispositionKind.AnythingLeftOver]:
          DispositiveProvisionsTemplateSlotKind.AbcGstNonExempt,
      };
      return {
        ...recipient,
        _templateSlotKind:
          slotKindByDisposition[
            recipient.dispositionKind_Select as DispositiveProvisionDispositionKind
          ] ?? DispositiveProvisionsTemplateSlotKind.Generic,
      };
    }

    if (kind === DispositiveProvisionTemplateKind.OutrightToSurvivingSpouse) {
      let slotKind = DispositiveProvisionsTemplateSlotKind.Generic;
      if (recipient.recipientKind === RecipientKind.SurvivingSpouse) {
        slotKind =
          DispositiveProvisionsTemplateSlotKind.OutrightSurvivingSpouse;
      } else if (
        includeDisclaimerTrust &&
        recipients.length === 2 &&
        index === 0
      ) {
        slotKind = DispositiveProvisionsTemplateSlotKind.OutrightDisclaimer;
      }
      return {
        ...recipient,
        _templateSlotKind: slotKind,
      };
    }

    if (kind === DispositiveProvisionTemplateKind.MaritalTrust) {
      let slotKind = DispositiveProvisionsTemplateSlotKind.Generic;
      if (
        recipient.dispositionKind_Select ===
        DispositiveProvisionDispositionKind.AnythingLeftOver
      ) {
        slotKind = DispositiveProvisionsTemplateSlotKind.MaritalTrust;
      }
      if (
        subKind === DispositiveProvisionTemplateSubKind.Disclaimer &&
        recipients.length === 2 &&
        index === 0 &&
        recipient.dispositionKind_Select ===
          DispositiveProvisionDispositionKind.RemainingLifetimeExclusionOfGrantor
      ) {
        slotKind = DispositiveProvisionsTemplateSlotKind.MaritalDisclaimer;
      } else if (
        subKind === DispositiveProvisionTemplateSubKind.ClaytonQtip &&
        recipients.length === 3 &&
        index === 0 &&
        recipient.dispositionKind_Select ===
          DispositiveProvisionDispositionKind.RemainingLifetimeExclusionOfGrantor
      ) {
        slotKind = DispositiveProvisionsTemplateSlotKind.MaritalBypass;
      } else if (
        subKind === DispositiveProvisionTemplateSubKind.ClaytonQtip &&
        recipients.length === 3 &&
        index === 1 &&
        recipient.dispositionKind_Select ===
          DispositiveProvisionDispositionKind.RemainingLifetimeExclusionOfGrantor
      ) {
        slotKind = DispositiveProvisionsTemplateSlotKind.MaritalClaytonQtip;
      }
      return { ...recipient, _templateSlotKind: slotKind };
    }

    const slotKind =
      kind === DispositiveProvisionTemplateKind.PouroverWill
        ? DispositiveProvisionsTemplateSlotKind.Pourover
        : DispositiveProvisionsTemplateSlotKind.Generic;
    return {
      ...recipient,
      _templateSlotKind: slotKind,
    };
  });
}

export function mapQueryDataToForm(
  templateData: DispositiveProvisionTemplateData_DispositiveProvisionTemplateFragment
): DispositiveProvisionsTemplateShape {
  let includeSurvivorsTrust = false;
  let includeDisclaimerTrust = false;
  let maritalTrustType = MaritalTrustType.None;

  const { kind, subKind } = templateData;
  if (kind === DispositiveProvisionTemplateKind.MaritalTrust) {
    if (subKind === DispositiveProvisionTemplateSubKind.ClaytonQtip) {
      maritalTrustType = MaritalTrustType.ClaytonQTIPTrust;
    } else if (subKind === DispositiveProvisionTemplateSubKind.Disclaimer) {
      maritalTrustType = MaritalTrustType.DisclaimerTrust;
    }
  } else if (kind === DispositiveProvisionTemplateKind.AbTrust) {
    if (subKind === DispositiveProvisionTemplateSubKind.Survivor) {
      includeSurvivorsTrust = true;
    }
  } else if (
    kind === DispositiveProvisionTemplateKind.OutrightToSurvivingSpouse
  ) {
    if (subKind === DispositiveProvisionTemplateSubKind.Disclaimer) {
      includeDisclaimerTrust = true;
    }
  }

  let recipients = compact(getNodes(templateData.dispositiveProvisions))
    .sort(sortDispositionOrder)
    .map(mapProvisionToRecipient)
    .map((recipient, idx) => {
      const isLastProvision =
        idx === getNodes(templateData.dispositiveProvisions).length - 1;

      // if there are any existing templates, force them to use the new surviving spouse recipient
      // for the last provision
      if (
        kind === DispositiveProvisionTemplateKind.OutrightToSurvivingSpouse &&
        isLastProvision
      ) {
        return {
          ...recipient,
          recipientId: SURVIVING_SPOUSE_SENTINEL,
          recipientKind: RecipientKind.SurvivingSpouse,
          transferTaxKind:
            DispositiveProvisionTransferTaxKind.SpouseMaritalExclusion,
        };
      }
      return recipient;
    });

  const hasExistingRecipients = recipients.length > 0;

  if (hasExistingRecipients) {
    recipients = assignTemplateSlotKinds(
      recipients,
      kind,
      subKind,
      includeDisclaimerTrust
    );
  }

  // If no existing provisions, apply template-specific defaults
  if (recipients.length === 0) {
    switch (templateData?.kind) {
      case DispositiveProvisionTemplateKind.PouroverWill:
        recipients = [BLANK_POUROVER_RECIPIENT];
        break;
      case DispositiveProvisionTemplateKind.OutrightToSurvivingSpouse:
        recipients = [BLANK_OUTRIGHT_TO_SURVIVING_SPOUSE];
        break;
      case DispositiveProvisionTemplateKind.MaritalTrust:
        recipients = [BLANK_MARITAL_TRUST];
        break;
      case DispositiveProvisionTemplateKind.AbcTrust:
        recipients = [...getDefaultAbcTrustRecipients()];
        break;
      case DispositiveProvisionTemplateKind.AbTrust:
        recipients = [...DEFAULT_AB_TRUST_RECIPIENTS];
        break;
      default:
        recipients = [defaultRecipient];
        break;
    }
  }

  if (templateData?.kind === DispositiveProvisionTemplateKind.AbTrust) {
    recipients = reconstructAbTrustRecipients(
      recipients,
      includeSurvivorsTrust
    );
  }

  return {
    [DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE]: {
      id: templateData.id,
      displayName: templateData.displayName,
      documentId: templateData.document?.id || undefined,
      includeDisclaimerTrust,
      includeSurvivorsTrust,
      maritalTrustType,
      proRata: templateData.proRata
        ? ProRataValue.PRO_RATA
        : ProRataValue.PER_ENTITY,
      templateType:
        templateData?.kind || DispositiveProvisionTemplateKind.Default,
    },
    [DISPOSITIVE_PROVISIONS_FORM_NAMESPACE]: {
      dispositionScenarioId: null,
      _dispositionScenarioAssociatedWaterfallId: null,
      selectedFirstPrimaryClientDeathId: null,
      _hasBeenReviewed: false,
      _residuaryPaysAllTaxes: templateData.residuaryPaysAllTaxes,
      dispositionScheme: DispositionScheme.UPON_FIRST_DEATH, // stub value; using NONE causes downstream errors
      recipients,
    },
  };
}

function getWhereSegment(id: string): [
  {
    hasDispositionScenariosWith: DispositionScenarioWhereInput[];
  },
] {
  return [
    {
      hasDispositionScenariosWith: [
        {
          or: [
            { hasDispositiveProvisionsTemplateWith: [{ id: id }] },
            {
              hasSecondDeathDispositiveProvisionsTemplateWith: [{ id: id }],
            },
          ],
        },
      ],
    },
  ];
}

export function getDispositiveProvisionTemplateUseCountEntityWhereInput(
  templateId: string
): EntityWhereInput {
  if (templateId === NEW_TEMPLATE_SENTINEL) {
    return {};
  }

  const whereSegment = getWhereSegment(templateId);

  const where: EntityWhereInput = {
    or: [
      { hasCcorpBusinessEntityWith: whereSegment },
      { hasCltTrustWith: whereSegment },
      { hasCrtTrustWith: whereSegment },
      { hasCustodialPersonalAccountWith: whereSegment },
      { hasDonorAdvisedFundWith: whereSegment },
      { hasGpBusinessEntityWith: whereSegment },
      { hasGratTrustWith: whereSegment },
      { hasIlitTrustWith: whereSegment },
      { hasIndividualPersonalAccountWith: whereSegment },
      { hasInsurancePersonalAccountWith: whereSegment },
      { hasIrrevocableTrustWith: whereSegment },
      { hasJointPersonalAccountWith: whereSegment },
      { hasLlcBusinessEntityWith: whereSegment },
      { hasLpBusinessEntityWith: whereSegment },
      { hasPrivateFoundationWith: whereSegment },
      { hasQprtTrustWith: whereSegment },
      { hasQualifiedTuitionPersonalAccountWith: whereSegment },
      { hasRetirementPersonalAccountWith: whereSegment },
      { hasRevocableTrustWith: whereSegment },
      { hasScorpBusinessEntityWith: whereSegment },
      { hasSlatTrustWith: whereSegment },
      { hasSoleProprietorshipBusinessEntityWith: whereSegment },
    ],
  };

  return where;
}

export function getDispositiveProvisionTemplateUseCountTestamentaryEntityWhereInput(
  templateId: string
): TestamentaryEntityWhereInput {
  if (templateId === NEW_TEMPLATE_SENTINEL) {
    return {};
  }

  return {
    hasDispositionScenariosWith: [
      {
        or: [
          { hasDispositiveProvisionsTemplateWith: [{ id: templateId }] },
          {
            hasSecondDeathDispositiveProvisionsTemplateWith: [
              { id: templateId },
            ],
          },
        ],
      },
    ],
  };
}

export function getDispositiveProvisionTemplateUseCountIndividualWhereInput(
  templateId: string,
  primaryClients: ContextPrimaryClient[] | null
): ClientProfileWhereInput {
  return {
    and: [
      {
        idIn: primaryClients?.map(({ id }) => id) || [],
      },
      {
        hasDispositionScenariosWith: [
          {
            or: [
              { hasDispositiveProvisionsTemplateWith: [{ id: templateId }] },
              {
                hasSecondDeathDispositiveProvisionsTemplateWith: [
                  { id: templateId },
                ],
              },
            ],
          },
        ],
      },
    ],
  };
}

function getDefaultDisplayName(templateType: DispositiveProvisionTemplateKind) {
  switch (templateType) {
    case DispositiveProvisionTemplateKind.PouroverWill:
      return 'Pourover will';
    case DispositiveProvisionTemplateKind.OutrightToSurvivingSpouse:
      return 'Outright to surviving spouse';
    case DispositiveProvisionTemplateKind.MaritalTrust:
      return 'Marital trust';
    case DispositiveProvisionTemplateKind.AbcTrust:
      return 'A/B/C trust';
    case DispositiveProvisionTemplateKind.AbTrust:
      return 'A/B trust';
    case DispositiveProvisionTemplateKind.Default:
      return 'Untitled template';
    default:
      throw new UnreachableError({
        case: templateType,
        message: 'Invalid template type provided',
      });
  }
}

export function getDefaultValues({
  templateId,
  templateType = DispositiveProvisionTemplateKind.Default,
  recipients: externalRecipients,
}: DispositiveProvisionTemplateDetails): DispositiveProvisionsTemplateShape {
  // get copy of array so the underlying values don't get modified
  let recipients: DispositiveProvisionsFormRecipient[];

  switch (templateType) {
    case DispositiveProvisionTemplateKind.PouroverWill:
      recipients = [BLANK_POUROVER_RECIPIENT];
      break;
    case DispositiveProvisionTemplateKind.OutrightToSurvivingSpouse:
      recipients = [BLANK_OUTRIGHT_TO_SURVIVING_SPOUSE];
      break;
    case DispositiveProvisionTemplateKind.MaritalTrust:
      recipients = [BLANK_MARITAL_TRUST];
      break;
    case DispositiveProvisionTemplateKind.AbcTrust:
      recipients = [...getDefaultAbcTrustRecipients()];
      break;
    case DispositiveProvisionTemplateKind.AbTrust:
      recipients = [...DEFAULT_AB_TRUST_RECIPIENTS];
      break;
    default:
      recipients = externalRecipients?.length
        ? [...externalRecipients]
        : [defaultRecipient];
      break;
  }

  return {
    [DISPOSITIVE_PROVISIONS_TEMPLATE_FORM_NAMESPACE]: {
      id: templateId,
      displayName: getDefaultDisplayName(templateType),
      documentId: '',
      templateType,
      includeDisclaimerTrust: false,
      includeSurvivorsTrust: false,
      maritalTrustType: MaritalTrustType.None,
      proRata: ProRataValue.PRO_RATA,
    },
    [DISPOSITIVE_PROVISIONS_FORM_NAMESPACE]: {
      dispositionScenarioId: null,
      _dispositionScenarioAssociatedWaterfallId: null,
      selectedFirstPrimaryClientDeathId: null,
      _hasBeenReviewed: false,
      _residuaryPaysAllTaxes: true, // Default to true for new templates
      dispositionScheme: DispositionScheme.UPON_FIRST_DEATH, // stub value; using NONE causes downstream errors
      recipients,
    },
  };
}

export function mapEntityDispositionScenariosToLinkedTable(
  entities: DispositiveProvisionTemplateUseCount_EntityFragment[] = [],
  templateId: string,
  setDispositiveProvisionsModalDetails: DispositiveProvisionsTemplateLinkedEntitiesProps['setDispositiveProvisionsModalDetails']
): DispositiveProvisionsTemplateLinkedEntryRow[] {
  return entities.reduce<DispositiveProvisionsTemplateLinkedEntryRow[]>(
    (acc, entity) => {
      const newRows = entity.subtype.dispositionScenarios
        ?.filter(
          (scenario) =>
            scenario.dispositiveProvisionsTemplate?.id === templateId ||
            scenario.secondDeathDispositiveProvisionsTemplate?.id === templateId
        )
        .map<DispositiveProvisionsTemplateLinkedEntryRow>((scenario) => ({
          lineOne: entity.subtype?.displayName || '',
          lineTwo: `${scenario.firstGrantorDeath?.displayGivenName} dies first (upon ${scenario.dispositiveProvisionsTemplate ? 'first' : 'second'} death)`,
          id: `${entity.id}-${scenario.id}`,
          onClick: () => {
            setDispositiveProvisionsModalDetails?.({
              type: DispositiveProvisionsModalDetailsType.ENTITY,
              entityId: entity.id,
              entityDisplayName: entity.subtype?.displayName || '',
              entitySubtypeId: entity.subtype.id,
              entityType: getEntityTypeFromSubtype(entity.subtype.__typename),
              firstDeathClientId: scenario.firstGrantorDeath.id,
              isFromTemplateEditor: true,
            });
          },
        }));

      return acc.concat(newRows || []);
    },
    []
  );
}

export function mapTestamentaryEntityScenariosToLinkedTable(
  testamentaryEntities: DispositiveProvisionTemplateUseCount_TestamentaryEntityFragment[] = [],
  templateId: string,
  setDispositiveProvisionsModalDetails: DispositiveProvisionsTemplateLinkedEntitiesProps['setDispositiveProvisionsModalDetails']
): DispositiveProvisionsTemplateLinkedEntryRow[] {
  return testamentaryEntities.reduce<
    DispositiveProvisionsTemplateLinkedEntryRow[]
  >((acc, testamentaryEntity) => {
    const newRows = testamentaryEntity.dispositionScenarios
      ?.filter(
        (scenario) =>
          scenario.dispositiveProvisionsTemplate?.id === templateId ||
          scenario.secondDeathDispositiveProvisionsTemplate?.id === templateId
      )
      .map<DispositiveProvisionsTemplateLinkedEntryRow>((scenario) => ({
        lineOne: testamentaryEntity.displayName || '',
        lineTwo: `${scenario.firstGrantorDeath?.displayGivenName} dies first (upon ${scenario.dispositiveProvisionsTemplate?.id === templateId ? 'first' : 'second'} death)`,
        id: `${testamentaryEntity.id}-${scenario.id}`,
        onClick: () => {
          setDispositiveProvisionsModalDetails?.({
            type: DispositiveProvisionsModalDetailsType.TESTAMENTARY_ENTITY,
            testamentaryEntityId: testamentaryEntity.id,
            entityDisplayName: testamentaryEntity.displayName || '',
            firstDeathClientId: scenario.firstGrantorDeath.id,
            isFromTemplateEditor: true,
          });
        },
      }));

    return acc.concat(newRows || []);
  }, []);
}

export function mapClientProfileScenariosToLinkedTable(
  clientProfiles: DispositiveProvisionTemplateUseCount_ClientProfileFragment[] = [],
  templateId: string,
  setDispositiveProvisionsModalDetails: DispositiveProvisionsTemplateLinkedEntitiesProps['setDispositiveProvisionsModalDetails']
): DispositiveProvisionsTemplateLinkedEntryRow[] {
  return clientProfiles.reduce<DispositiveProvisionsTemplateLinkedEntryRow[]>(
    (acc, individual) => {
      const newRows = individual.dispositionScenarios
        ?.filter(
          (scenario) =>
            scenario.dispositiveProvisionsTemplate?.id === templateId ||
            scenario.secondDeathDispositiveProvisionsTemplate?.id === templateId
        )
        .map<DispositiveProvisionsTemplateLinkedEntryRow>((scenario) => ({
          lineOne: individual.displayName || '',
          lineTwo: `${scenario.firstGrantorDeath?.displayGivenName} dies first (upon ${scenario.dispositiveProvisionsTemplate ? 'first' : 'second'} death)`,
          id: `${individual.id}-${scenario.id}`,
          onClick: () => {
            setDispositiveProvisionsModalDetails?.({
              type: DispositiveProvisionsModalDetailsType.CLIENT_PROFILE,
              clientProfileId: individual.id,
              displayName: individual.displayName,
              valueToDistribute: new Decimal(0),
              firstDeathClientId: scenario.firstGrantorDeath.id,
              isFromTemplateEditor: true,
            });
          },
        }));
      return acc.concat(newRows || []);
    },
    []
  );
}
